import {
  type CheckboxChangeEvent,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Row,
  Select,
  Tooltip,
} from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { difference } from 'lodash';
import React, {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DeliveryOrganizationChooser } from 'components/choosers/DeliveryOrganizationChooser';
import { StyledInfoCircle } from 'components/choosers/DeliveryOrganizationChooser/DeliveryOrganizationChooser.style';
import { TooltipWithQuestionIcon } from 'components/ui';
import {
  type AtlasGqlMetadataField,
  type AtlasGqlMetadataFieldMapping,
  AtlasGqlDecimalSeparator,
  AtlasGqlFileFieldDelimiter,
  AtlasGqlFileFormat,
  AtlasGqlInspectionStatus,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { useFeatures } from 'utils/features';
import { ENHANCED_INSPECTION_STATUSES } from 'utils/release-toggles';

import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { type Steps } from '../UploadInspectionDataModal';
import {
  AutoReleaseStatusSelect,
  ButtonContainer,
  CheckboxFieldMappingInputColumn,
  ConfigurationText,
  FieldMappingInputColumn,
  FieldMappingLabelColumn,
  FieldMappingRow,
  VerticalDivider,
} from '../UploadInspectionDataModal.style';

type FieldMappingProps = {
  fieldMappings?: AtlasGqlMetadataFieldMapping[];
  fileType: AtlasGqlFileFormat;
  ignoreRequired?: boolean;
  internalFields: AtlasGqlMetadataField[];
  /**
   * If provided, form will error and not submit unless a mapping for at least one of the
   * field names in each set is made; each array is a set of independent requirements
   *
   * e.g. [['id', 'taskId']]
   */
  requireAtLeastOneOf?: string[][];
  providedFieldNames: string[];
  setFieldMappings: Dispatch<SetStateAction<AtlasGqlMetadataFieldMapping[] | undefined>>;
  previousStep: Steps;
  nextStep: Steps;
  setStep: Dispatch<SetStateAction<Steps>>;
  likelyDelimiterError: boolean;
  cellDelimiter: AtlasGqlFileFieldDelimiter;
  setCellDelimiter: Dispatch<SetStateAction<AtlasGqlFileFieldDelimiter>>;
  decimalDelimiter: AtlasGqlDecimalSeparator;
  setDecimalDelimiter: Dispatch<SetStateAction<AtlasGqlDecimalSeparator>>;
  forOrganizationRef?: MutableRefObject<string | null>;
  skipTaskCreationRef?: MutableRefObject<boolean>;
};

export const FieldMappingForm: React.FC<FieldMappingProps> = ({
  fieldMappings,
  fileType,
  ignoreRequired,
  internalFields,
  requireAtLeastOneOf,
  providedFieldNames,
  setFieldMappings,
  previousStep,
  nextStep,
  setStep,
  likelyDelimiterError,
  cellDelimiter,
  setCellDelimiter,
  decimalDelimiter,
  setDecimalDelimiter,
  forOrganizationRef,
  skipTaskCreationRef,
}) => {
  const { t } = useTranslation();
  const [form] = useForm<{ [key: string]: string }>();
  const [usedFieldNames, setUsedFieldNames] = useState<string[]>([]);
  const hasErrors = form.getFieldsError().some(({ errors }) => !!errors.length);
  const [skipTaskCreation, setSkipTaskCreation] = useState<boolean>(
    skipTaskCreationRef?.current ?? false
  );

  const { INSPECTION_DELIVERY } = useFeatures().features;

  const { hasReleaseToggle } = useAccountContext();
  const hasEnhancedStatuses = hasReleaseToggle(ENHANCED_INSPECTION_STATUSES);
  const { shouldAutoRelease, setShouldAutoRelease, autoReleaseStatus, setAutoReleaseStatus } =
    useUploadInspectionDataModalContext();

  /**
   * Initialize form with any existing values (if user has gone back and forth) or
   * any field names that match the system defaults
   */
  const initialValues = useMemo(() => {
    if (fieldMappings) {
      return Object.fromEntries(
        fieldMappings.map(({ sourceFieldName, targetFieldName }) => [
          targetFieldName,
          sourceFieldName,
        ])
      );
    }

    return Object.fromEntries(
      internalFields.map((field) => [
        field.internalName,
        providedFieldNames.find((name) =>
          [field.defaultCsvHeader, field.defaultJsonFieldName].includes(name)
        ),
      ])
    );
  }, [fieldMappings, internalFields, providedFieldNames]);

  useEffect(() => {
    form.resetFields();
  }, [form, initialValues]);

  const availableFieldOptions = useMemo(
    () =>
      likelyDelimiterError
        ? []
        : difference(providedFieldNames, usedFieldNames).map((h: string) => ({
            label: h,
            value: h,
          })),
    [providedFieldNames, usedFieldNames, likelyDelimiterError]
  );

  useEffect(() => {
    setUsedFieldNames(Object.values(initialValues).filter((name): name is string => !!name));
  }, [initialValues]);

  const taskIdValue = useWatch('taskId', form);
  const taskIdMapped = useMemo(() => !!taskIdValue, [taskIdValue]);

  useEffect(() => {
    if (taskIdMapped && forOrganizationRef) {
      form.setFieldsValue({ forOrganization: undefined });
      forOrganizationRef.current = null;
    }
  }, [taskIdMapped, form, forOrganizationRef]);

  const validInternalNames = useMemo(
    () => new Set(internalFields.map((f) => f.internalName)),
    [internalFields]
  );

  const handleSelect = () => {
    const values = form.getFieldsValue();
    setUsedFieldNames(
      Object.entries(values)
        .filter(
          ([targetFieldName, sourceFieldName]) =>
            !!sourceFieldName && validInternalNames.has(targetFieldName)
        )
        .map(([_targetFieldName, sourceFieldName]) => sourceFieldName)
    );
  };

  const handleSubmit = (values: { [key: string]: string }) => {
    if (forOrganizationRef) {
      forOrganizationRef.current = skipTaskCreation
        ? null
        : (form.getFieldValue('forOrganization') ?? null);
    }

    setFieldMappings(
      Object.entries(values)
        .filter(
          ([targetFieldName, sourceFieldName]) =>
            !!sourceFieldName && validInternalNames.has(targetFieldName)
        )
        .map(([targetFieldName, sourceFieldName]) => ({ sourceFieldName, targetFieldName }))
    );

    form.resetFields();
    setStep(nextStep);
  };

  const values = form.getFieldsValue();
  const requirementMessages = useMemo(() => {
    if (requireAtLeastOneOf?.length) {
      return requireAtLeastOneOf.reduce<{ [key: string]: string }>((messages, requiredFieldSet) => {
        const displayNames = requiredFieldSet.map(
          (internalName) =>
            internalFields.find((f) => internalName === f.internalName)?.displayName ?? internalName
        );
        const quantifier =
          displayNames.length === 2 ? t('inspection.both', 'both') : t('inspection.all', 'all');
        const message = t(
          'inspection.fieldsCannotBeEmpty',
          '{{fields}} cannot {{quantifier}} be empty',
          { fields: displayNames.join(', '), quantifier }
        );
        return {
          ...messages,
          ...Object.fromEntries(requiredFieldSet.map((field) => [field, message])),
        };
      }, {});
    }
    return {};
  }, [internalFields, requireAtLeastOneOf, t]);
  const requirementsSatisfied = useMemo(() => {
    if (requireAtLeastOneOf?.length) {
      return requireAtLeastOneOf
        .map((requiredFieldSet) =>
          Object.entries(values).some(
            ([targetFieldName, sourceFieldName]) =>
              requiredFieldSet.includes(targetFieldName) && !!sourceFieldName
          )
        )
        .every((satisfied) => satisfied);
    }
    return true;
  }, [requireAtLeastOneOf, values]);

  const handleSkipTaskCreateCheckboxChange = (e: CheckboxChangeEvent) => {
    const value = e.target.checked;
    if (value) {
      form.setFieldsValue({ forOrganization: undefined });
      if (forOrganizationRef) {
        forOrganizationRef.current = null;
      }
    }

    setSkipTaskCreation(value);
    if (skipTaskCreationRef) {
      skipTaskCreationRef.current = value;
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFieldMappings(undefined);
    setStep(previousStep);
    if (forOrganizationRef) {
      forOrganizationRef.current = null;
    }
    if (skipTaskCreationRef) {
      skipTaskCreationRef.current = false;
    }
  };

  const forOrganizationInput = !taskIdMapped && INSPECTION_DELIVERY && forOrganizationRef && (
    <Row key={'forOrganization'} justify={'space-between'}>
      <Col flex="100%">
        <Form.Item label={t('inspection.forOrganization', 'For Organization')}>
          <DeliveryOrganizationChooser
            formKey="forOrganization"
            form={form}
            assetIds={[]}
            defaultShowAllOrgs={true}
            defaultOrgId={forOrganizationRef.current ?? undefined}
          />
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      scrollToFirstError={true}
      initialValues={initialValues}
    >
      <Row gutter={{ sm: 24 }} wrap={false}>
        <Col flex="1 1 0%">
          <ConfigurationText>
            {t('inspection.configureSource', 'Configure Source')}
          </ConfigurationText>
          {internalFields.map((field) => (
            <FieldMappingRow key={field.id}>
              <FieldMappingLabelColumn>
                <label>
                  <span className="field-mapping-label-text">
                    {field.displayName}
                    {field.required && !ignoreRequired && (
                      <span className="field-mapping-required-indicator">*</span>
                    )}
                    :
                  </span>
                </label>
              </FieldMappingLabelColumn>
              <FieldMappingInputColumn>
                <Form.Item
                  key={field.internalName}
                  name={field.internalName}
                  rules={[
                    {
                      required: (field.required && !ignoreRequired) ?? false,
                      message: t('inspection.fieldCannotBeEmpty', '{{field}} cannot be empty', {
                        field: field.displayName ?? field.internalName,
                      }),
                      whitespace: true,
                    },
                  ]}
                  {...(requirementMessages[field.internalName] && !requirementsSatisfied
                    ? {
                        validateStatus: 'error',
                        help: requirementMessages[field.internalName],
                        style: { marginBottom: '0px' },
                      }
                    : {})}
                >
                  <Select
                    data-testid={`inspection-mapping-field-${field.internalName}`}
                    placeholder={t('inspection.selectSourceField', 'Select source {{fieldType}}', {
                      fieldType:
                        fileType === AtlasGqlFileFormat.Csv
                          ? t('inspection.column', 'column')
                          : t('inspection.field', 'field'),
                    })}
                    allowClear
                    options={availableFieldOptions}
                    onChange={handleSelect}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                  />
                </Form.Item>
              </FieldMappingInputColumn>
            </FieldMappingRow>
          ))}
        </Col>
        <VerticalDivider />
        <Col flex="1 1 0%">
          <ConfigurationText>
            {t('inspection.configureDelimiters', 'Configure Delimiters')}
            {likelyDelimiterError && (
              <p className="warning-text">
                {t(
                  'inspection.delimiterWarning',
                  'It appears your csv has delimiters other than {{delimiter}}s. Please select the appropriate delimiter before mapping fields.',
                  { delimiter: cellDelimiter.toLowerCase() }
                )}
              </p>
            )}
          </ConfigurationText>
          {fileType === AtlasGqlFileFormat.Csv && (
            <FieldMappingRow key={'cellDelimiter'} $labelColumnWidth="40%">
              <FieldMappingLabelColumn>
                <label>
                  <span className="field-mapping-label-text">
                    {t('inspection.cellDelimiter', 'Cell Delimiter:')}
                  </span>
                </label>
              </FieldMappingLabelColumn>
              <FieldMappingInputColumn>
                <Form.Item>
                  <Select
                    data-testid={`inspection-mapping-cell-delimiter`}
                    allowClear={false}
                    value={cellDelimiter}
                    onChange={(value) => {
                      form.resetFields();
                      setCellDelimiter(value);
                    }}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                    options={[
                      {
                        label: t('inspection.commaDefault', 'Comma - Default'),
                        value: AtlasGqlFileFieldDelimiter.Comma,
                        disabled: decimalDelimiter === AtlasGqlDecimalSeparator.Comma,
                      },
                      {
                        label: t('inspection.semicolon', 'Semicolon'),
                        value: AtlasGqlFileFieldDelimiter.Semicolon,
                      },
                      {
                        label: t('inspection.tab', 'Tab'),
                        value: AtlasGqlFileFieldDelimiter.Tab,
                      },
                    ]}
                  />
                </Form.Item>
              </FieldMappingInputColumn>
            </FieldMappingRow>
          )}
          <FieldMappingRow key={'decimalDelimiter'} $labelColumnWidth="40%">
            <FieldMappingLabelColumn>
              <label>
                <span className="field-mapping-label-text">
                  {t('inspection.decimalDelimiter', 'Decimal Delimiter:')}
                </span>
              </label>
            </FieldMappingLabelColumn>
            <FieldMappingInputColumn>
              <Form.Item>
                <Select
                  data-testid={`inspection-mapping-decimal-delimiter`}
                  allowClear={false}
                  value={decimalDelimiter}
                  onChange={setDecimalDelimiter}
                  getPopupContainer={(triggerNode) => triggerNode.parentElement}
                  options={[
                    {
                      label: t('inspection.periodDefault', 'Period - Default'),
                      value: AtlasGqlDecimalSeparator.Period,
                    },
                    {
                      label: t('inspection.comma', 'Comma'),
                      value: AtlasGqlDecimalSeparator.Comma,
                      disabled:
                        fileType === AtlasGqlFileFormat.Csv &&
                        cellDelimiter === AtlasGqlFileFieldDelimiter.Comma,
                    },
                  ]}
                />
              </Form.Item>
            </FieldMappingInputColumn>
          </FieldMappingRow>
          <Row>
            <Col flex="100%">
              {skipTaskCreationRef && (
                <>
                  <Form.Item label={t('inspection.taskCreation', 'Task Creation')}>
                    <Checkbox
                      data-testid="skip-task-create-checkbox"
                      checked={skipTaskCreation}
                      onChange={handleSkipTaskCreateCheckboxChange}
                    >
                      <span>
                        <span>{t('inspection.skipTaskCreation', 'Skip Task Creation')}</span>
                        <Tooltip
                          title={t(
                            'inspection.skipTaskCreationTooltip',
                            "By default Tasks will be auto created for all Inspection records which don't have mapped Task ID. You can skip Task creation using this option."
                          )}
                        >
                          <StyledInfoCircle />
                        </Tooltip>
                      </span>
                    </Checkbox>
                  </Form.Item>
                  {!skipTaskCreation && forOrganizationInput}
                </>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <Divider />
      <Row gutter={{ sm: 24 }}>
        <Col flex="100%">
          <ConfigurationText>
            {t('inspection.releaseSettings', 'Release Settings')}
          </ConfigurationText>
          <FieldMappingRow key="autoRelease" $labelColumnWidth="20%">
            <FieldMappingLabelColumn>
              <label htmlFor="auto-release-checkbox">
                <span className="field-mapping-label-text">
                  {t('inspection.automaticallyReleaseLabel', 'Automatically Release:')}
                </span>
              </label>
            </FieldMappingLabelColumn>
            <CheckboxFieldMappingInputColumn>
              <Checkbox
                id="auto-release-checkbox"
                checked={shouldAutoRelease}
                onChange={() => setShouldAutoRelease((current) => !current)}
              >
                <TooltipWithQuestionIcon
                  message={t(
                    'inspection.autoReleaseInspectionsTooltip',
                    'When this box is unchecked, inspections will remain in the Draft state until they are released by a user.'
                  )}
                />
              </Checkbox>
            </CheckboxFieldMappingInputColumn>
          </FieldMappingRow>
          {hasEnhancedStatuses && (
            <FieldMappingRow key="autoReleaseStatus" $labelColumnWidth="20%">
              <FieldMappingLabelColumn>
                <label>
                  <span className="field-mapping-label-text">{t('table.status', 'Status')}:</span>
                </label>
              </FieldMappingLabelColumn>
              <FieldMappingInputColumn>
                <AutoReleaseStatusSelect
                  data-testid="auto-release-status-select"
                  value={autoReleaseStatus}
                  onChange={setAutoReleaseStatus}
                  disabled={!shouldAutoRelease}
                  style={{ width: 'fit-content', minWidth: '10rem' }}
                  options={[
                    {
                      value: AtlasGqlInspectionStatus.AnalysisPending,
                      label: 'Analysis Pending',
                    },
                    {
                      value: AtlasGqlInspectionStatus.AnalysisComplete,
                      label: 'Analysis Complete',
                    },
                  ]}
                />
                <TooltipWithQuestionIcon
                  message={t(
                    'inspection.autoReleaseStatusTooltip',
                    'Status the Inspection will be set to after release is completed'
                  )}
                  iconStyle={{ marginLeft: '0.5rem' }}
                />
              </FieldMappingInputColumn>
            </FieldMappingRow>
          )}
        </Col>
      </Row>

      <ButtonContainer>
        <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
        <Button
          type="primary"
          htmlType="submit"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            form.submit();
          }}
          disabled={hasErrors || !requirementsSatisfied}
        >
          {t('inspection.preview', 'Preview')}
        </Button>
      </ButtonContainer>
    </Form>
  );
};
